import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ApiResponse, ApiResponsePagination } from "@/types/api";
import { getQueryKey } from "@key.ai/constants";
import { cleanObject } from "@/lib/helper";
import eventClientApi from "@/api-client/event-client";
import { useSession } from "next-auth/react";

export const useEventTickets = ({
  eventId = "",
  enabled
}: {
  eventId: string;
  enabled: boolean;
}) => {
  const queryKey = getQueryKey("EVENT_TICKETS", { eventId });
  const { data: user, status: authStatus } = useSession();
  const fetchEventTickets = async ({ pageParam = 1 }: { pageParam: unknown }) => {
    const { data } = await eventClientApi
      .get(`api/v1${authStatus !== "authenticated" ? "/public" : ""}/events/${eventId}/tickets`, {
        searchParams: cleanObject({
          page: pageParam as string,
          limit: 10
        })
      })
      .json<ApiResponse<any>>();
    return { ...data, count: data?.count, pageParam };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isLoading, refetch } =
    useInfiniteQuery<ApiResponsePagination<any>>({
      queryKey: [queryKey, eventId, enabled],
      queryFn: fetchEventTickets,
      getNextPageParam: (lastPage: any) => {
        if (Math.ceil(lastPage?.count / 10) > lastPage?.pageParam) {
          return lastPage?.pageParam + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      gcTime: 0,
      refetchOnMount: true,
      refetchInterval: false,
      enabled: enabled && !!eventId
    });

  const count = data?.pages?.[0]?.count ?? 0;

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
    refetch,
    count
  };
};
